import { create } from 'zustand';
import { messages, type MessageKey } from '../i18n';
import { es } from '../i18n/es';
import { pl } from '../i18n/pl';
import { ro } from '../i18n/ro';
import { uk } from '../i18n/uk';
import {
  isLocaleId,
  LOCALE_IDS,
  LOCALE_NAMES,
  LOCALE_TAGS,
  localeFromLanguage,
  splitLocalePath,
  type LocaleId,
} from '@bannermatic/core';
import { publicPageLocales } from '../marketing/catalog';

export type Locale = LocaleId;

export const LOCALES: Array<{ id: Locale; name: string; short: string }> = LOCALE_IDS.map((id) => ({
  id,
  name: LOCALE_NAMES[id],
  short: id.toUpperCase(),
}));

export const isLocale = isLocaleId;

/** BCP 47 tag of the current language, for dates and numbers. */
export const localeTag = (locale: Locale) => LOCALE_TAGS[locale];

/** The visitor's language when the URL doesn't name one: their earlier choice, else the browser's. */
export function preferredLocale(): Locale {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('bm.locale') : null;
  if (isLocale(saved)) return saved;
  const langs =
    typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : [];
  for (const lang of langs) {
    const match = localeFromLanguage(lang);
    if (match) return match;
  }
  return 'en';
}

function initialLocale(): Locale {
  if (typeof window === 'undefined') return preferredLocale();
  // Public pages carry the language in the path: "/uk/…"; English has no prefix.
  const { locale, path, prefixed } = splitLocalePath(window.location.pathname);
  if (prefixed) {
    localStorage.setItem('bm.locale', locale);
    return locale;
  }
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (isLocale(urlLang)) {
    localStorage.setItem('bm.locale', urlLang);
    return urlLang;
  }
  const preferred = preferredLocale();
  // An English public page stays English unless it redirects to the visitor's language.
  const pageLocales = publicPageLocales(path);
  if (pageLocales && !pageLocales.includes(preferred)) return 'en';
  return preferred;
}

/** Translations kept as separate tables; English and Russian live in ../i18n/index. */
const TABLES: Partial<Record<Locale, Record<MessageKey, string>>> = { uk, pl, ro, es };

const init = initialLocale();
if (typeof document !== 'undefined') {
  document.documentElement.lang = init;
}

export const useLocale = create<{
  locale: Locale;
  /** Switches the language; `remember: false` shows a page in it without changing the choice. */
  setLocale(l: Locale, remember?: boolean): void;
}>((set) => ({
  locale: init,
  setLocale(locale, remember = true) {
    if (remember) localStorage.setItem('bm.locale', locale);
    document.documentElement.lang = locale;
    set({ locale });
  },
}));

/** Saves the visitor's language choice without switching the current page. */
export function rememberLocale(locale: Locale) {
  localStorage.setItem('bm.locale', locale);
}

export function getLocale(): Locale {
  return useLocale.getState().locale;
}

export type Vars = Record<string, string | number>;

export function translate(locale: Locale, key: MessageKey, vars?: Vars): string {
  const entry = messages[key];
  let text: string = !entry ? key : (TABLES[locale]?.[key] ?? entry[locale === 'ru' ? 0 : 1]);
  if (vars) for (const [k, v] of Object.entries(vars)) text = text.replaceAll(`{${k}}`, String(v));
  return text;
}

export function t(key: MessageKey, vars?: Vars): string {
  return translate(getLocale(), key, vars);
}

/** Hook version: re-renders the component when the language changes. */
export function useT() {
  const locale = useLocale((s) => s.locale);
  return (key: MessageKey, vars?: Vars) => translate(locale, key, vars);
}

export type { MessageKey };
